import { CIRX_ANONYMOUS_TOKEN_EXPIRATION, CIRX_ANONYMOUS_USER, TYPESENSE_TOKEN_KEY_PREFIX } from "@/core/constants/api";
import { Api } from "@/core/services/api.service";
import Cookie from "@/assets/js/components/cookie";
import { getCurrentDomain, isPublicApp } from "@/core/services/domain.service";
import useTypesenseStore from "@/apps/shared/catalog/core/infrastructure/store/typesenseStore";
import { storeToRefs } from "pinia";
import useAccountStore from "@/apps/shared/organization/store/accountStore";
import identificationService from "@/core/services/identification.service";
import { jwtDecode } from "jwt-decode";

const TypesenseClientService = {
  /**
   * Main method to search data in Typesense
   *
   * @param {string} indexPrefix
   * @param {object} searchParameters
   *
   * @see https://typesense.org/docs/0.22.2/api/documents.html#search-parameters
   *
   * @returns {SearchResponse}
   */
  async search(indexPrefix, searchParameters) {
    if (!indexPrefix || !searchParameters) {
      throw Error("Please specify index and search parameters");
    }

    // Currently, used for the warehouse check
    const typesenseStore = useTypesenseStore();
    const { organizationIndexName } = storeToRefs(typesenseStore);

    const { id: accountId } = useAccountStore();
    const indexOrganizationId = organizationIndexName.value ?? accountId;

    const client = await this._createClient(indexOrganizationId);
    const typesenseDocuments = client
      .collections(import.meta.env.VITE_APP_TYPESENSE_INDEX_PREFIX + indexPrefix + indexOrganizationId)
      .documents();

    let i = 0;
    let tryAgain = false;

    do {
      tryAgain = false;
      try {
        return await typesenseDocuments.search(searchParameters, {});
      } catch (e) {
        tryAgain = true;
        i++;

        if (i < 6) {
          await new Promise((r) => setTimeout(() => r(), 200));
        } else if (i > 4) {
          throw new Error("Issue with Typesense, can't connect to the server", { cause: e });
        }
      }
    } while (tryAgain);
  },

  /**
   * @param {string} organizationId
   * @returns {Promise<string>}
   */
  async getTokenForUser(organizationId) {
    const typesenseTokenResponse = await Api.new().get(`/organizations/${organizationId}/typesense`);

    const typesenseToken = typesenseTokenResponse.data.token;
    Cookie.setCookie(`${TYPESENSE_TOKEN_KEY_PREFIX}-${organizationId}`, typesenseToken, {
      "max-age": CIRX_ANONYMOUS_TOKEN_EXPIRATION,
    });

    return typesenseToken;
  },

  clearToken() {
    const { id: accountId } = useAccountStore();
    if (accountId) {
      Cookie.deleteCookie(`${TYPESENSE_TOKEN_KEY_PREFIX}-${accountId}`);
      Cookie.deleteCookie(`${TYPESENSE_TOKEN_KEY_PREFIX}-rps-${accountId}`);
    }
  },

  async _getTokenForPublicApp() {
    let anonymousToken = Cookie.getCookie(CIRX_ANONYMOUS_USER);
    if (!anonymousToken) {
      await identificationService.getAnonymousUserWithORD(getCurrentDomain());
      anonymousToken = Cookie.getCookie(CIRX_ANONYMOUS_USER);
    }

    return jwtDecode(anonymousToken).typesense_key;
  },

  /**
   * @param {string} indexOrganizationId
   * @returns {Promise<SearchClient>}
   * @private
   */
  async _createClient(indexOrganizationId) {
    const hosts = import.meta.env.VITE_APP_TYPESENSE_API_HOST.split(",");
    const nodes = hosts.map((host) => {
      return {
        host,
        port: import.meta.env.VITE_APP_TYPESENSE_API_PORT,
        protocol: import.meta.env.VITE_APP_TYPESENSE_API_PROTOCOL,
      };
    });

    const moduleTypeSense = await import("typesense");
    const TypesenseSearchClient = moduleTypeSense.SearchClient;

    return new TypesenseSearchClient({
      nodes,
      apiKey: await this._getApiKey(indexOrganizationId),
      connectionTimeoutSeconds: 2,
    });
  },

  /**
   * @param {string} indexOrganizationId
   * @returns {Promise<string>}
   * @private
   */
  async _getApiKey(indexOrganizationId) {
    const tokenFromCookie = Cookie.getCookie(`${TYPESENSE_TOKEN_KEY_PREFIX}-${indexOrganizationId}`);
    if (tokenFromCookie) {
      return tokenFromCookie;
    }

    const userStore = useTypesenseStore();
    const { setAsLoading, setAsFinished } = userStore;

    setAsLoading();
    const token = isPublicApp() ? await this._getTokenForPublicApp() : await this.getTokenForUser(indexOrganizationId);
    setAsFinished();

    return token;
  },
};

export default TypesenseClientService;
